import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import { TrendingUp, Clock, Calendar, Award, Trophy, Users, Medal } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { getBeltName, getBeltBadgeColors } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default chart colors (lighter versions for chart fills)
const DEFAULT_CHART_COLORS = {
  white: { fill: '#e5e7eb', stroke: '#9ca3af' },
  yellow: { fill: '#fef08a', stroke: '#eab308' },
  orange: { fill: '#fed7aa', stroke: '#f97316' },
  green: { fill: '#bbf7d0', stroke: '#22c55e' },
  blue: { fill: '#bfdbfe', stroke: '#3b82f6' },
  purple: { fill: '#e9d5ff', stroke: '#a855f7' },
  brown: { fill: '#fde68a', stroke: '#d97706' },
  black_1: { fill: '#d1d5db', stroke: '#374151' },
  black_2: { fill: '#d1d5db', stroke: '#374151' },
  black_3: { fill: '#d1d5db', stroke: '#374151' },
  black_4: { fill: '#d1d5db', stroke: '#374151' },
  black_5: { fill: '#d1d5db', stroke: '#374151' },
};

// Get chart colors - uses belt config border_color for stroke, derives fill
const getBeltChartColor = (belt, getBeltBySlug) => {
  // Try to get from belt config
  if (getBeltBySlug) {
    const beltConfig = getBeltBySlug(belt);
    if (beltConfig) {
      // Use first color from belt colors array for stroke
      const primaryColor = beltConfig.colors?.[0]?.color || '#808080';
      // Create a lighter fill by using opacity
      return {
        fill: `${primaryColor}30`, // 30 = ~19% opacity in hex
        stroke: beltConfig.border_color || primaryColor,
      };
    }
  }
  // Fallback to default chart colors
  return DEFAULT_CHART_COLORS[belt] || DEFAULT_CHART_COLORS.white;
};

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

// Custom tooltip for the chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/90 dark:bg-stone-900/90 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700">
        <p className="font-semibold text-stone-800 dark:text-stone-100 mb-1">{label}</p>
        <div className="space-y-1 text-sm">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-stone-600 dark:text-stone-400">{entry.name}:</span>
              <span className="font-medium text-stone-800 dark:text-stone-200">
                {entry.name.includes('Hours') ? `${entry.value} hrs` : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const TrainingProgressGraph = ({ student }) => {
  const { getBeltBySlug, getBeltName: contextGetBeltName } = useBelts();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cumulative'); // 'cumulative' or 'monthly'
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    if (student?.id) {
      fetchTrainingHistory();
    }
  }, [student?.id]);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') ||
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    // Watch for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', checkDarkMode);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', checkDarkMode);
    };
  }, []);

  const fetchTrainingHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/training-history`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch training history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!student) {
    return null;
  }

  if (loading) {
    return (
      <Card className="member-card">
        <CardContent className="py-8 text-center text-stone-500 dark:text-stone-400">
          Loading training history...
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.history || data.history.length === 0) {
    return (
      <Card className="member-card">
        <CardHeader className="pb-2 border-b border-stone-100 dark:border-stone-700 bg-gradient-to-r from-transparent to-amber-50/30 dark:to-amber-900/10">
          <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <TrendingUp className="h-5 w-5 text-amber-600" />
            Training Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="text-center py-8 text-stone-500 dark:text-stone-400">
            <TrendingUp className="h-12 w-12 mx-auto mb-3 text-stone-300 dark:text-stone-600" />
            <p>No training history yet.</p>
            <p className="text-sm mt-1">Start attending classes to see your progress!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data based on view mode
  const chartData = data.history.map(entry => ({
    ...entry,
    classes: viewMode === 'cumulative' ? entry.cumulative_classes : entry.classes,
    hours: viewMode === 'cumulative' ? entry.cumulative_hours : entry.hours
  }));

  // Build belt periods for shaded areas
  // Start with white belt from beginning, then each promotion changes the belt
  const beltPeriods = [];
  const promotionMilestones = data.milestones.filter(m => m.type === 'promotion');
  const sortedMilestones = [...promotionMilestones].sort((a, b) => a.month.localeCompare(b.month));

  if (chartData.length > 0) {
    // Determine starting belt (belt before first promotion, or current belt if no promotions)
    let currentBelt = 'white';
    if (sortedMilestones.length > 0 && sortedMilestones[0].from_belt) {
      currentBelt = sortedMilestones[0].from_belt;
    }

    let startLabel = chartData[0].label;

    sortedMilestones.forEach((milestone, idx) => {
      const dataIndex = chartData.findIndex(d => d.month === milestone.month);
      if (dataIndex >= 0) {
        // Add period for the previous belt
        beltPeriods.push({
          x1: startLabel,
          x2: chartData[dataIndex].label,
          belt: currentBelt,
        });
        // Update for next period
        currentBelt = milestone.to_belt;
        startLabel = chartData[dataIndex].label;
      }
    });

    // Add final period (current belt to end)
    if (startLabel !== chartData[chartData.length - 1].label || beltPeriods.length === 0) {
      beltPeriods.push({
        x1: startLabel,
        x2: chartData[chartData.length - 1].label,
        belt: currentBelt,
      });
    }
  }

  return (
    <Card className="member-card">
      <CardHeader className="pb-2 border-b border-stone-100 dark:border-stone-700 bg-gradient-to-r from-transparent to-amber-50/30 dark:to-amber-900/10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-200">
            <TrendingUp className="h-5 w-5 text-amber-600" />
            Training Progress
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'cumulative' ? 'memberPrimary' : 'memberSecondary'}
              size="sm"
              onClick={() => setViewMode('cumulative')}
            >
              Total
            </Button>
            <Button
              variant={viewMode === 'monthly' ? 'memberPrimary' : 'memberSecondary'}
              size="sm"
              onClick={() => setViewMode('monthly')}
            >
              Monthly
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="member-stat-card flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-800 dark:text-stone-200">{data.total_classes}</p>
              <p className="text-xs text-stone-500 dark:text-stone-400">Total Classes</p>
            </div>
          </div>
          <div className="member-stat-card flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <Clock className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-stone-800 dark:text-stone-200">{data.total_hours}</p>
              <p className="text-xs text-stone-500 dark:text-stone-400">Total Hours</p>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="h-72 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: isDarkMode ? '#d1d5db' : '#57534e' }}
                tickLine={false}
                axisLine={{ stroke: isDarkMode ? '#374151' : '#e5e7eb' }}
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12, fill: isDarkMode ? '#d1d5db' : '#57534e' }}
                tickLine={false}
                axisLine={{ stroke: isDarkMode ? '#374151' : '#e5e7eb' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />

              {/* Belt period shaded areas */}
              {beltPeriods.map((period, idx) => {
                const colors = getBeltChartColor(period.belt, getBeltBySlug);
                return (
                  <ReferenceArea
                    key={`area-${idx}`}
                    x1={period.x1}
                    x2={period.x2}
                    yAxisId="left"
                    fill={colors.fill}
                    fillOpacity={0.3}
                  />
                );
              })}

              {/* Promotion reference lines with belt colors */}
              {data.milestones.filter(m => m.type === 'promotion').map((milestone, idx) => {
                const dataIndex = chartData.findIndex(d => d.month === milestone.month);
                if (dataIndex >= 0) {
                  const colors = getBeltChartColor(milestone.to_belt, getBeltBySlug);
                  return (
                    <ReferenceLine
                      key={`promo-${idx}`}
                      x={chartData[dataIndex].label}
                      stroke={colors.stroke}
                      strokeWidth={2}
                      yAxisId="left"
                    />
                  );
                }
                return null;
              })}

              {/* Tournament reference lines (purple with trophy icon style) */}
              {data.milestones.filter(m => m.type === 'tournament').map((milestone, idx) => {
                const dataIndex = chartData.findIndex(d => d.month === milestone.month);
                if (dataIndex >= 0) {
                  return (
                    <ReferenceLine
                      key={`tournament-${idx}`}
                      x={chartData[dataIndex].label}
                      stroke="#9333ea"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      yAxisId="left"
                    />
                  );
                }
                return null;
              })}

              {/* Seminar reference lines (teal) */}
              {data.milestones.filter(m => m.type === 'seminar').map((milestone, idx) => {
                const dataIndex = chartData.findIndex(d => d.month === milestone.month);
                if (dataIndex >= 0) {
                  return (
                    <ReferenceLine
                      key={`seminar-${idx}`}
                      x={chartData[dataIndex].label}
                      stroke="#0d9488"
                      strokeWidth={2}
                      strokeDasharray="3 3"
                      yAxisId="left"
                    />
                  );
                }
                return null;
              })}

              <Line
                yAxisId="left"
                type="monotone"
                dataKey="classes"
                name={viewMode === 'cumulative' ? 'Total Classes' : 'Monthly Classes'}
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="hours"
                name={viewMode === 'cumulative' ? 'Total Hours' : 'Monthly Hours'}
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Milestones Legend */}
        {data.milestones.filter(m => m.type === 'promotion').length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
            <p className="text-sm font-medium text-stone-700 dark:text-stone-200 mb-2 flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-600" />
              Belt Promotions
            </p>
            <div className="flex flex-wrap gap-2">
              {data.milestones.filter(m => m.type === 'promotion').map((milestone, idx) => {
                const beltColors = getBeltBadgeColors(milestone.to_belt);
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs border"
                    style={{
                      backgroundColor: beltColors.bg,
                      borderColor: beltColors.border,
                    }}
                  >
                    <span style={{ color: beltColors.text }}>{milestone.label}</span>
                    <span style={{ color: beltColors.text, opacity: 0.5 }}>-</span>
                    <span className="font-medium" style={{ color: beltColors.text }}>
                      {getBeltName(milestone.to_belt, milestone.to_stripes)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tournaments Legend */}
        {data.milestones.filter(m => m.type === 'tournament').length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
            <p className="text-sm font-medium text-stone-700 dark:text-stone-200 mb-2 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-purple-600" />
              Tournaments
            </p>
            <div className="space-y-2">
              {data.milestones.filter(m => m.type === 'tournament').map((tournament, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700"
                >
                  <div className="flex-1">
                    <span className="text-sm font-medium text-purple-900 dark:text-purple-300">{tournament.name}</span>
                    <span className="text-xs text-purple-600 dark:text-purple-400 ml-2">
                      {tournament.label}
                      {tournament.location && ` • ${tournament.location}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {tournament.gold > 0 && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700">
                        <Medal className="h-3 w-3" />
                        {tournament.gold} Gold
                      </span>
                    )}
                    {tournament.silver > 0 && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 border border-stone-300 dark:border-stone-600">
                        <Medal className="h-3 w-3" />
                        {tournament.silver} Silver
                      </span>
                    )}
                    {tournament.bronze > 0 && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                        <Medal className="h-3 w-3" />
                        {tournament.bronze} Bronze
                      </span>
                    )}
                    {tournament.gold === 0 && tournament.silver === 0 && tournament.bronze === 0 && tournament.results?.length > 0 && (
                      <span className="text-xs text-purple-600 dark:text-purple-400">Participated</span>
                    )}
                  </div>
                  {tournament.notes && (
                    <p className="text-xs text-purple-600 dark:text-purple-400 italic mt-1 sm:mt-0 sm:ml-2">{tournament.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seminars Legend */}
        {data.milestones.filter(m => m.type === 'seminar').length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
            <p className="text-sm font-medium text-stone-700 dark:text-stone-200 mb-2 flex items-center gap-2">
              <Users className="h-4 w-4 text-teal-600" />
              Seminars
            </p>
            <div className="flex flex-wrap gap-2">
              {data.milestones.filter(m => m.type === 'seminar').map((seminar, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 px-2 py-1 rounded text-xs bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700"
                >
                  <span className="text-teal-700 dark:text-teal-300">{seminar.label}</span>
                  <span className="text-teal-500 dark:text-teal-400">-</span>
                  <span className="font-medium text-teal-800 dark:text-teal-200">{seminar.name}</span>
                  {seminar.duration_hours && (
                    <span className="text-teal-500 dark:text-teal-400">({seminar.duration_hours}h)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrainingProgressGraph;
